// Định dạng số / nhãn của trang Phân tích Creator (đặc tả §8): số kiểu Việt `1.234.567`, giờ `1.234,5 giờ`,
// % 1 số lẻ `+12,3%`. Tăng xanh #16a34a, giảm đỏ #dc2626, giữ xám — LUÔN kèm mũi tên + chữ (không chỉ màu).

import {
  ANALYTICS_ERROR_TEXT,
  CompareMode,
  AnalyticsApiError,
  CreatorKind,
  CreatorRef,
  PaceStatus,
  Trend,
} from './contract';

export const COLOR_UP = '#16a34a';
export const COLOR_DOWN = '#dc2626';
export const COLOR_FLAT = '#6b7280';

const intFormat = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 });
const oneDecimal = new Intl.NumberFormat('vi-VN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
const fixedOne = new Intl.NumberFormat('vi-VN', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Số nguyên kiểu Việt: 1234567 → "1.234.567"; null → "—". */
export function fmtNum(value: number | null | undefined): string {
  return isNum(value) ? intFormat.format(value) : '—';
}

/** Kim cương: cùng định dạng số nguyên. */
export const fmtKc = fmtNum;

/**
 * Trục biểu đồ, gọn: 1.234.567 → "1,2tr"; 12.400 → "12k"; 950 → "950". Làm tròn TRƯỚC khi chọn đơn vị để
 * 999.600 → "1tr" (không phải "1.000k") và 999,6 → "1k".
 */
export function fmtCompact(value: number | null | undefined): string {
  if (!isNum(value)) return '—';
  const n = Math.round(value);
  if (Math.abs(n) < 1000) return intFormat.format(n);
  const k = Math.round(n / 1e3);
  if (Math.abs(k) < 1000) return `${intFormat.format(k)}k`;
  return `${oneDecimal.format(Math.round(n / 1e5) / 10)}tr`;
}

/** Số tối đa 1 số lẻ kiểu Việt (trục giờ / Creator): 1200 → "1.200"; 2.5 → "2,5". */
export function fmtDec(value: number | null | undefined): string {
  return isNum(value) ? oneDecimal.format(value) : '—';
}

/** Giờ (đã là giờ, có thể lẻ): 1234.5 → "1.234,5 giờ"; 12 → "12 giờ". */
export function fmtHours(hours: number | null | undefined): string {
  return isNum(hours) ? `${oneDecimal.format(hours)} giờ` : '—';
}

/** Giây → giờ, 1 số lẻ. */
export function secondsToHours(seconds: number | null | undefined): number {
  return isNum(seconds) ? Math.round((seconds / 3600) * 10) / 10 : 0;
}

/** % đã ×100: 12.34 → "+12,3%"; -4 → "-4,0%"; 0 → "0,0%"; null → "—". */
export function fmtPct(pct: number | null | undefined): string {
  if (!isNum(pct)) return '—';
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) return `${fixedOne.format(0)}%`;
  const sign = rounded > 0 ? '+' : '-';
  return `${sign}${fixedOne.format(Math.abs(rounded))}%`;
}

/** Tỷ trọng (0–100) không dấu: 33.333 → "33,3%". */
export function fmtShare(pct: number | null | undefined): string {
  return isNum(pct) ? `${fixedOne.format(pct)}%` : '—';
}

export interface DirectionMeta {
  arrow: '▲' | '▼' | '●';
  color: string;
  /** Chữ đi kèm (đọc được khi không thấy màu). */
  word: string;
}

const UP: DirectionMeta = { arrow: '▲', color: COLOR_UP, word: 'Tăng' };
const DOWN: DirectionMeta = { arrow: '▼', color: COLOR_DOWN, word: 'Giảm' };
const FLAT: DirectionMeta = { arrow: '●', color: COLOR_FLAT, word: 'Giữ' };
const NA: DirectionMeta = { arrow: '●', color: COLOR_FLAT, word: 'Chưa đủ dữ liệu' };

/** Hướng theo dấu của % (dòng phụ KPI "so … ngày đầu tháng trước"). */
export function pctDirection(pct: number | null | undefined): DirectionMeta {
  if (!isNum(pct)) return NA;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded > 0) return UP;
  if (rounded < 0) return DOWN;
  return FLAT;
}

/** Thẻ xu hướng 7 ngày: trend do backend chấm (ngưỡng trendPct), % để hiện kèm. */
export function trendTag(
  trend: Trend | null | undefined,
  pct: number | null | undefined
): DirectionMeta & { text: string } {
  const meta = trend === 'UP' ? UP : trend === 'DOWN' ? DOWN : trend === 'FLAT' ? FLAT : NA;
  const pctText = isNum(pct) ? fmtPct(pct) : '';
  const text = meta === NA ? `${meta.arrow} ${meta.word}` : `${meta.arrow} ${pctText || meta.word}`;
  return { ...meta, text };
}

export const KIND_LABEL: Record<CreatorKind, string> = {
  ONLINE: 'Online',
  STUDIO: 'Studio',
  NPC: 'NPC',
  DANCE: 'Nhóm nhảy',
};

export const KIND_OPTIONS: { label: string; value: CreatorKind | 'ALL' }[] = [
  { label: 'Tất cả', value: 'ALL' },
  { label: 'Online', value: 'ONLINE' },
  { label: 'Studio', value: 'STUDIO' },
  { label: 'NPC', value: 'NPC' },
  { label: 'Nhóm nhảy', value: 'DANCE' },
];

/** Màu antd Tag + chữ cho trạng thái tiến độ tới bậc kế tiếp. */
export const PACE_META: Record<PaceStatus, { label: string; color: string }> = {
  DONE: { label: 'Đã đủ bậc', color: 'success' },
  AHEAD: { label: 'Vượt tiến độ', color: 'success' },
  ON_TRACK: { label: 'Đúng tiến độ', color: 'processing' },
  BEHIND: { label: 'Chậm tiến độ', color: 'warning' },
  UNREACHABLE: { label: 'Khó kịp', color: 'error' },
};

/**
 * "Để đạt Bậc X còn thiếu N ngày · H giờ · K KC — cần ~k KC/ngày" (đặc tả §8). Phần đã đủ (= 0) bị bỏ;
 * "cần ~k KC/ngày" chỉ khi còn thiếu KC. Không thiếu gì → null (nơi gọi hiện "Đã đủ điều kiện Bậc X").
 */
export function goalGapText(
  level: number,
  pace: {
    needDays: number;
    needSeconds: number;
    needKc: number;
    requiredKcPerDay: number | null;
  }
): string | null {
  const parts: string[] = [];
  if (pace.needDays > 0) parts.push(`${fmtNum(pace.needDays)} ngày`);
  if (pace.needSeconds > 0) parts.push(fmtHours(secondsToHours(pace.needSeconds)));
  if (pace.needKc > 0) parts.push(`${fmtKc(pace.needKc)} KC`);
  if (!parts.length) return null;
  const perDay =
    pace.needKc > 0 && isNum(pace.requiredKcPerDay) && pace.requiredKcPerDay > 0
      ? ` — cần ~${fmtKc(Math.ceil(pace.requiredKcPerDay))} KC/ngày`
      : '';
  return `Để đạt Bậc ${level} còn thiếu ${parts.join(' · ')}${perDay}`;
}

/** "2026-10" → "Tháng 10/2026". */
export function fmtMonth(month: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month || '');
  return m ? `Tháng ${Number(m[2])}/${m[1]}` : month || '—';
}

/** "2026-10-05" → "05/10". */
export function fmtDay(day: string | null | undefined): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(day || '');
  return m ? `${m[2]}/${m[1]}` : day || '—';
}

/** Thứ trong tuần, thứ Hai = 0 … Chủ nhật = 6 (lịch heatmap). */
export function weekdayMon0(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return (dow + 6) % 7;
}

/** "@tiktokid" nếu có, không thì "UID 123…". */
export function creatorName(ref: Pick<CreatorRef, 'uid' | 'username'>): string {
  return ref.username ? `@${ref.username}` : `UID ${ref.uid}`;
}

/** Câu lỗi để hiện: theo `code` của backend trước, rồi `message`. */
export function errorText(err: unknown, fallback = 'Không tải được dữ liệu phân tích.'): string {
  const e = (err || {}) as AnalyticsApiError;
  if (e.code && ANALYTICS_ERROR_TEXT[e.code]) return ANALYTICS_ERROR_TEXT[e.code];
  if (typeof e.message === 'string' && e.message) return e.message;
  return fallback;
}

/** Mã lỗi nghiệp vụ (nếu có). */
export function errorCode(err: unknown): string | null {
  const code = (err as AnalyticsApiError | null)?.code;
  return typeof code === 'string' ? code : null;
}

/** Câu mặc định theo chế độ so tháng trước khi backend không gửi `label`. */
export const COMPARE_LABEL: Record<CompareMode, string> = {
  SAME_DAYS: 'so cùng số ngày đầu tháng trước',
  TIKTOK_MONTH_FILE: 'Cả tháng so với tháng trước (theo file tháng TikTok)',
  PROJECTED_VS_PREV_MONTH_FILE: 'Dự kiến cả tháng so với tháng trước',
};

/**
 * Nguồn của dòng so sánh. SAME_DAYS (hoặc đợt 1: không có chế độ mà có n) → "so {n} ngày đầu {tháng trước}";
 * chế độ khác → câu của backend (`compare.label`), thiếu thì câu mặc định.
 */
export function compareText(
  mode: CompareMode | null | undefined,
  source: string | null | undefined,
  n: number | null | undefined,
  monthLabel = 'tháng trước'
): string {
  if ((mode === 'SAME_DAYS' || !mode) && n) return `so ${n} ngày đầu ${monthLabel}`;
  if (mode && mode !== 'SAME_DAYS') return source || COMPARE_LABEL[mode];
  return source || `so ${monthLabel}`;
}

/** % thay đổi (đã ×100, 1 số lẻ) như backend pctChange: gốc ≤ 0 hoặc thiếu → null. */
export function pctChange(
  curr: number | null | undefined,
  prev: number | null | undefined
): number | null {
  if (!isNum(curr) || !isNum(prev) || !(prev > 0)) return null;
  const v = Math.round(((curr - prev) / prev) * 1000) / 10;
  return v === 0 ? 0 : v;
}

/** "trên 2.406 Creator" khi phép so chỉ cộng một phần phạm vi; đủ cả phạm vi → null. */
export function compareCreatorsNote(
  compared: number | null | undefined,
  scope: number | null | undefined
): string | null {
  if (!isNum(compared) || !isNum(scope) || compared >= scope) return null;
  return `trên ${fmtNum(compared)} Creator`;
}

/** Tháng đã kết thúc: cờ `monthEnded` của backend; server / ảnh chụp cũ không có → `daysRemaining === 0`. */
export function isMonthEnded(
  monthEnded: boolean | null | undefined,
  daysRemaining: number | null | undefined
): boolean {
  return monthEnded ?? daysRemaining === 0;
}

/** Không có số tháng trước cho chỉ số này (Fan mới, KC PK — file tháng trước không có cột). */
export const NO_PREV_TEXT = 'Không có số tháng trước';
/** Chưa so được và backend không nói lý do (`compareNote` = null). */
export const NO_COMPARE_TEXT = 'Chưa so được với tháng trước';

/** PROJECTED mà chưa đủ file ngày cả tháng (nhãn không bắt đầu "Cả tháng") → số tháng này là DỰ KIẾN. */
export function isProjected(mode: CompareMode | null | undefined, label?: string | null): boolean {
  return mode === 'PROJECTED_VS_PREV_MONTH_FILE' && !(label || '').startsWith('Cả tháng');
}

/** Tiêu đề cột "Tháng này" của bảng so tháng trước. */
export function currColumnTitle(mode: CompareMode | null | undefined, label?: string | null) {
  return isProjected(mode, label) ? 'Tháng này (dự kiến)' : 'Tháng này';
}

/** Phạm vi của file tháng TikTok: giai đoạn ("01/09–27/09") khi KHÔNG trọn tháng, "cả tháng …" khi trọn. */
export function monthFileScope(
  period: string | null | undefined,
  partial: boolean,
  asOfDay?: string | null
): string {
  if (partial) return period || (asOfDay ? `đến ${fmtDay(asOfDay)}` : 'một phần tháng');
  return period ? `cả tháng ${period}` : 'cả tháng';
}
